import * as React from "react";
import {
  flexRender,
  type Header,
  type HeaderGroup,
} from "@tanstack/react-table";
import { cn } from "../../lib/utils";
import { Checkbox } from "../Checkbox";
import { tableHeaderVariants } from "./tableVariants";

interface TableHeaderProps<TData> {
  headerGroups: HeaderGroup<TData>[];
  enableRowSelection: boolean;
  enableSelectAll: boolean;
  showHeaderBackground: boolean;
  stickyHeader: boolean;
  size: "small" | "medium" | "large" | "auto";
  headerClassName?: string;
  onToggleAllRows: (e?: unknown) => void;
  isAllRowsSelected: boolean;
  isSomeRowsSelected: boolean;
  getColumnStyle: (
    columnId: string,
    fallback?: { width?: number; minWidth?: number; maxWidth?: number }
  ) => React.CSSProperties;
}

export function TableHeader<TData>({
  headerGroups,
  enableRowSelection,
  enableSelectAll,
  showHeaderBackground,
  stickyHeader,
  size,
  headerClassName,
  onToggleAllRows,
  isAllRowsSelected,
  isSomeRowsSelected,
  getColumnStyle,
}: TableHeaderProps<TData>) {
  const headerHeight =
    {
      small: 32,
      medium: 40,
      large: 48,
      auto: 56,
    }[size] ?? 40;

  return (
    <thead
      className={cn(
        showHeaderBackground ? "bg-surface-fill-neutral-moderate" : "bg-white",
        stickyHeader && "sticky top-0 z-10"
      )}
    >
      {headerGroups.map((headerGroup, groupIndex) => {
        const stickyTop = stickyHeader ? groupIndex * headerHeight : undefined;

        return (
          <tr key={headerGroup.id}>
            {enableRowSelection && enableSelectAll && (
              <th
                className={cn(
                  tableHeaderVariants({ size }),
                  showHeaderBackground && "bg-surface-fill-neutral-moderate",
                  stickyHeader && "sticky z-20",
                  "w-10 rounded-tl-xlarge rounded-bl-xlarge",
                  headerClassName
                )}
                style={{ top: stickyTop }}
              >
                <Checkbox
                  checked={isAllRowsSelected}
                  isIndeterminate={isSomeRowsSelected}
                  onChange={onToggleAllRows}
                  aria-label="Select all rows"
                />
              </th>
            )}
            {headerGroup.headers.map((header, index) => {
              const isFirstColumn = index === 0;
              const isLastColumn = index === headerGroup.headers.length - 1;

              return (
                <th
                  key={header.id}
                  className={cn(
                    tableHeaderVariants({ size }),
                    showHeaderBackground &&
                      "bg-surface-fill-neutral-moderate border-none",
                    stickyHeader && "sticky z-20",
                    !enableRowSelection && index === 0 && "rounded-tl-xlarge ",
                    isLastColumn && "rounded-tr-xlarge",
                    header.column.columnDef.meta?.headerClassName,
                    headerClassName
                  )}
                  style={{
                    ...getColumnStyle(header.column.id, {
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                    }),
                    top: stickyTop,
                    ...(isFirstColumn && { paddingLeft: "24px" }),
                    ...(isLastColumn && { paddingRight: "24px" }),
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      className={cn(
                        "flex items-center gap-2",
                        header.column.getCanSort() &&
                          "cursor-pointer select-none"
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getCanSort() && (
                        <span className="text-surface-ink-neutral-muted">
                          {{
                            asc: "↑",
                            desc: "↓",
                          }[header.column.getIsSorted() as string] ?? "↕"}
                        </span>
                      )}
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        );
      })}
    </thead>
  );
}
